import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";
import {
  usePaymentBanking,
  usePaymentBankingStore,
  useCustomer,
  useBankAccount,
} from "hooks";
import { PaymentBankingQuery, PaymentBankingRow } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { compareDate, formatDate, parseId, parseNumber } from "utils/helpers/data_helpers";

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeItems = (items?: PaymentBankingRow["items"]) =>
  Array.isArray(items)
    ? items
      .map((item) => ({
        amount: parseNumber(item.amount),
        partner_id: parseId(item.partner_id),
        expense_reason_id: parseId(item.expense_reason_id),
        description: item.description
          ? String(item.description).trim()
          : undefined,
      }))
      .filter(
        (item) =>
          item.amount ||
          item.partner_id ||
          item.expense_reason_id ||
          Boolean(item.description),
      )
    : [];

const withItemFields = (banking: PaymentBankingRow): PaymentBankingRow => {
  const normalizedItems = sanitizeItems(banking.items);
  const firstItem = normalizedItems[0];
  return {
    ...banking,
    bank_account_id: parseId(banking.bank_account_id),
    bank_id: parseId(banking.bank_id),
    import_invoice_id: parseId(banking.import_invoice_id),
    attachments: normalizeAttachments(banking.attachments),
    items: normalizedItems,
    amount:
      banking.amount ??
      (typeof firstItem?.amount === "number"
        ? firstItem.amount
        : firstItem?.amount
          ? Number(firstItem.amount)
          : undefined),
    partner_id: parseId(banking.partner_id ?? firstItem?.partner_id),
    expense_reason_id: parseId(
      banking.expense_reason_id ?? firstItem?.expense_reason_id,
    ),
    item_description: banking.item_description ?? firstItem?.description,
  };
};

const PaymentBankings: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<PaymentBankingQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<PaymentBankingRow | null>(null);
  const [selectedItems, setSelectedItems] = useState<
    NonNullable<PaymentBankingRow["items"]>
  >([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");

  const { rows, setRows, removeRow, isLoading, setLoading } =
    usePaymentBankingStore();

  const { editingId } = usePaymentBankingStore();

  const { fetchList, deletePaymentBanking } = usePaymentBanking();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );
  const { data: bankAccountsData } = useBankAccount().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const {
    data: paymentBankingData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  useEffect(() => {
    if (paymentBankingData?.data) {
      const mapped =
        (paymentBankingData.data as PaymentBankingRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
      if (mapped.length) {
        setSelectedItems(mapped[0].items ?? []);
        setSelectedLabel(
          mapped[0].payment_code ??
          mapped[0].receiver_name ??
          `Phiếu ${mapped[0].id}`,
        );
      } else {
        setSelectedItems([]);
        setSelectedLabel("");
      }
    }
  }, [paymentBankingData, setRows]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deletePaymentBanking.isPending));
  }, [deletePaymentBanking.isPending, isFetching, setLoading]);

  const customerLabelById = useMemo(() => {
    const map = new Map<number | string, string>();
    (customersData?.data as any[])?.forEach((c) => {
      const label = `${c.code}${c.name ? ` - ${c.name}` : ""}`;
      map.set(c.id, label);
    });
    return map;
  }, [customersData]);

  const bankAccountsList =
    (Array.isArray(bankAccountsData?.data)
      ? bankAccountsData?.data
      : Array.isArray((bankAccountsData as any)?.data?.data)
        ? (bankAccountsData as any)?.data?.data
        : []) ?? [];

  const bankAccountNumberById = useMemo(() => {
    const map = new Map<number | string, string>();
    bankAccountsList.forEach((b: any) => {
      map.set(
        b.id,
        `${b.account_number ?? b.account_name ?? ""}${
          /* b.bank_name ? ` - ${b.bank_name}` : */ ""
        }`,
      );
    });
    return map;
  }, [bankAccountsList]);
  const bankNameById = useMemo(() => {
    const map = new Map<number | string, string>();
    bankAccountsList.forEach((b: any) => {
      const bankId = b.bank_id ?? b.id;
      map.set(bankId, b.bank_name ?? `Ngân hàng ${bankId}`);
    });
    return map;
  }, [bankAccountsList]);

  const columns = useMemo<ColDef<PaymentBankingRow>[]>(() => {
    return [
      {
        headerName: "Mã phiếu",
        field: "payment_code",
        minWidth: 140,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Ngày chi",
        field: "payment_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Tài khoản NH",
        field: "bank_account_id",
        minWidth: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return "";
          return (
            bankAccountNumberById.get(params.value) ?? String(params.value)
          );
        },
      },
      {
        headerName: "Ngân hàng",
        field: "bank_account_id",
        minWidth: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return "";
          return bankNameById.get(params.value) ?? String(params.value);
        },
      },
      {
        headerName: "Người nộp",
        field: "trader_name",
        flex: 1,
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Người nhận",
        field: "receiver_name",
        flex: 1,
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Tài khoản nhận",
        field: "receiver_account",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Ngân hàng nhận",
        field: "receiver_bank",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Hóa đơn nhập",
        field: "import_invoice_id",
        minWidth: 140,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Tệp đính kèm",
        field: "attachments",
        minWidth: 160,
        filter: "agTextColumnFilter",
        valueFormatter: (params) =>
          Array.isArray(params.value)
            ? params.value.join(", ")
            : (params.value ?? ""),
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 140,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "",
      },
      {
        headerName: "Đối tác",
        field: "partner_id",
        minWidth: 180,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return "";
          const match = (customersData?.data as any[])?.find(
            (c) => String(c.id) === String(params.value),
          );
          return match
            ? `${match.code}${match.name ? ` - ${match.name}` : ""}`
            : String(params.value);
        },
      },
    ];
  }, [bankAccountNumberById, bankNameById]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const handleAdd = useCallback(() => {
    navigate(ROUTES.ACCOUNTING.BANK_PAYMENT_CREATE);
  }, [navigate]);

  const handleEditRow = useCallback(
    (params: any) => {
      if (params?.data?.id) {
        navigate(`${ROUTES.ACCOUNTING.BANK_PAYMENT_DETAIL}/${params.data.id}`);
      }
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<PaymentBankingRow>) => {
      const items = sanitizeItems(event?.data?.items);
      setSelectedItems(items);
      setSelectedLabel(
        event?.data?.payment_code ??
        event?.data?.receiver_name ??
        `Phiếu ${event?.data?.id ?? ""}`,
      );
    },
    [],
  );

  const handleDeleteRow = useCallback((row: PaymentBankingRow) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deletePaymentBanking.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
        refetch();
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu chi ngân hàng không thành công");
      },
    });
  }, [confirmRow, deletePaymentBanking, refetch, removeRow]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) =>
        params.data?.id === editingId || params.node.rowPinned === "top",
      flex: 1,
      minWidth: 140,
    }),
    [editingId],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Kế toán] Phiếu chi ngân hàng
        </h1>
      </div>

      <div className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-end">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 text-green-700" />
              Thêm phiếu chi NH
            </button>
          </div>
        </div>

        <div className="mb-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Chi tiết dòng chi {selectedLabel ? `- ${selectedLabel}` : ""}
            </h3>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Số tiền
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Đối tác
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Lý do chi
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">Mô tả</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedItems.length ? (
                  selectedItems.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {Number(item.amount ?? 0).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.partner_id
                          ? (customerLabelById.get(item.partner_id) ??
                            String(item.partner_id))
                          : ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.expense_reason_id ?? ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.description ?? ""}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chọn một phiếu chi ngân hàng để xem chi tiết dòng chi.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={paymentBankingData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          getRowId={(params) => String(params.data.id)}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu chi ngân hàng, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu chi ngân hàng?"
        description={`Bạn chắc chắn muốn xóa phiếu chi ngân hàng "${confirmRow?.payment_code ?? ""
          }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deletePaymentBanking.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default PaymentBankings;
